import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  BottomSheetFlatList,
  BottomSheetTextInput,
} from '@gorhom/bottom-sheet';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import PhoneInput, {
  type ICountry,
  type IPhoneInputRef,
} from 'rn-international-phone-number';
import { GTFonts, PoppinsFonts, useTheme } from '../../theme';
import { borderWidth, fs, s, v } from '../../utils/libs';
import type { AssignableStaffMember } from '../../api/employees';
import { useDetectedCountry } from '../../hooks/useDetectedCountry';
import {
  buildE164FromInput,
  PREFERRED_COUNTRIES,
} from '../../utils/phone';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const STAFF_CIRCLE_COLORS = [
  '#DBEAFE', // light blue
  '#E2E8F0', // light grey
  '#DBEAFE', // light blue
  '#E8DEF8', // light lavender
  '#FFDDD2', // light peach
  '#D1FAE5', // light mint
  '#FEF3C7', // light yellow
  '#FCE7F3', // light pink
];

const AVATAR_INITIALS_COLOR = '#1E3A5F';

const getStaffCircleColor = (index: number) =>
  STAFF_CIRCLE_COLORS[index % STAFF_CIRCLE_COLORS.length];

/**
 * Colour is derived from the name rather than the row index so a customer keeps
 * the same avatar tint while the list is being filtered.
 */
const getCustomerCircleColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash + name.charCodeAt(i)) % STAFF_CIRCLE_COLORS.length;
  }
  return STAFF_CIRCLE_COLORS[hash];
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const WALK_IN_CUSTOMER = 'Walk in customer';

const MAX_DROPDOWN_ITEMS = 6;
const CUSTOMER_ITEM_HEIGHT = 44;

type CustomerItem = {
  name: string;
  count: number;
  customerId?: string | null;
  isCloud?: boolean;
};

interface NewCustomerFormData {
  name: string;
  phone: string;
  email: string;
}

interface OrderCustomerStaffSectionProps {
  input: string;
  isLoadingOrder: boolean;
  showCustomerDropdown: boolean;
  filteredCustomers: CustomerItem[];
  setInput: (value: string) => void;
  onCustomerInputFocus: () => void;
  isCustomerInputEditable?: boolean;
  onSubmitName: () => void;
  onSelectCustomer: (name: string, customerId?: string | null) => void;
  shouldShowStaffSection: boolean;
  staffQuery: string;
  setStaffQuery: (value: string) => void;
  isLoadingStaff: boolean;
  filteredStaff: AssignableStaffMember[];
  assignedStaffId: string | null;
  toggleStaffSelection: (id: string) => void;
  showAddNewCustomerChip?: boolean;
  onCreateCustomer?: (data: NewCustomerFormData) => Promise<void>;
  isCreatingCustomer?: boolean;
  onNewFormVisibilityChange?: (visible: boolean) => void;
}

const OrderCustomerStaffSection = ({
  input,
  isLoadingOrder,
  showCustomerDropdown,
  filteredCustomers,
  setInput,
  onCustomerInputFocus,
  isCustomerInputEditable = true,
  onSubmitName,
  onSelectCustomer,
  shouldShowStaffSection,
  staffQuery,
  setStaffQuery,
  isLoadingStaff,
  filteredStaff,
  assignedStaffId,
  toggleStaffSelection,
  showAddNewCustomerChip,
  onCreateCustomer,
  isCreatingCustomer,
  onNewFormVisibilityChange,
}: OrderCustomerStaffSectionProps) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const canEditCustomerInput = isCustomerInputEditable && !isLoadingOrder;
  const normalizedInput = input.trim().toLowerCase();
  const isWalkInSelected = normalizedInput === WALK_IN_CUSTOMER.toLowerCase();

  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPhoneInput, setNewPhoneInput] = useState('');
  const [newPhoneE164, setNewPhoneE164] = useState('');
  const [newPhoneCountry, setNewPhoneCountry] = useState<ICountry | undefined>(
    undefined,
  );
  const [newPhoneValid, setNewPhoneValid] = useState(false);
  const [newPhoneTouched, setNewPhoneTouched] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const phoneInputRef = useRef<IPhoneInputRef | null>(null);
  const { countryCode: detectedCountryCode, iCountry } = useDetectedCountry();

  useEffect(() => {
    if (showNewForm && !newPhoneCountry && iCountry) {
      setNewPhoneCountry(iCountry);
    }
  }, [showNewForm, newPhoneCountry, iCountry]);

  useEffect(() => {
    setNewPhoneValid(!!phoneInputRef.current?.isValidPhoneNumber);
  }, [newPhoneInput, newPhoneE164]);

  const newFormNameValid = newName.trim().length >= 2;
  const newFormEmailValid =
    !newEmail.trim() || EMAIL_REGEX.test(newEmail.trim());
  const canSubmitNewCustomer =
    newFormNameValid && newPhoneValid && newFormEmailValid && !isCreatingCustomer;

  const handleCancelNewForm = () => {
    setShowNewForm(false);
    onNewFormVisibilityChange?.(false);
    setNewName('');
    setNewPhoneInput('');
    setNewPhoneE164('');
    setNewPhoneCountry(undefined);
    setNewPhoneValid(false);
    setNewPhoneTouched(false);
    setNewEmail('');
  };

  const handleSubmitNewCustomer = async () => {
    if (!canSubmitNewCustomer || !onCreateCustomer) return;
    await onCreateCustomer({
      name: newName.trim(),
      phone: newPhoneE164,
      email: newEmail.trim(),
    });
    handleCancelNewForm();
  };

  // "Walk in customer" is the implicit default rather than a real suggestion, so
  // it is promoted to a quick-pick chip and kept out of the suggestion list.
  const customerSuggestions = useMemo(
    () =>
      filteredCustomers.filter(
        c => c.name.trim().toLowerCase() !== WALK_IN_CUSTOMER.toLowerCase(),
      ),
    [filteredCustomers],
  );

  const isDropdownOpen =
    canEditCustomerInput &&
    showCustomerDropdown &&
    customerSuggestions.length > 0;

  const renderCustomerItem = ({ item }: { item: CustomerItem }) => {
    const isSelected = normalizedInput === item.name.trim().toLowerCase();
    const orderCount = item.count || 0;

    return (
      <TouchableOpacity
        activeOpacity={0.6}
        style={[styles.customerItem, isSelected && styles.customerItemSelected]}
        onPress={() => onSelectCustomer(item.name, item.customerId ?? null)}>
        <View
          style={[
            styles.customerAvatar,
            { backgroundColor: getCustomerCircleColor(item.name) },
          ]}>
          <Text style={styles.customerAvatarText}>
            {getInitials(item.name)}
          </Text>
          {item.isCloud ? (
            <View style={styles.cloudBadge}>
              <FontAwesome6
                name="cloud"
                iconStyle="solid"
                size={fs(7)}
                color="#5B93D3"
              />
            </View>
          ) : null}
        </View>
        <Text
          style={[styles.customerName, isSelected && styles.customerNameSelected]}
          numberOfLines={1}>
          {item.name}
        </Text>
        {isSelected ? (
          <FontAwesome6
            name="check"
            iconStyle="solid"
            size={fs(12)}
            color={theme.colors.primaryDark}
          />
        ) : orderCount > 0 ? (
          <Text style={styles.customerMeta}>
            {orderCount} {orderCount === 1 ? 'order' : 'orders'}
          </Text>
        ) : null}
      </TouchableOpacity>
    );
  };

  const renderStaffItem = ({
    item,
    index,
  }: {
    item: AssignableStaffMember;
    index: number;
  }) => {
    const isSelected = assignedStaffId === item.id;

    return (
      <Pressable
        style={styles.staffChipWrap}
        onPress={() => toggleStaffSelection(item.id)}>
        <View
          style={[
            styles.staffCircle,
            { backgroundColor: getStaffCircleColor(index) },
            isSelected && styles.staffCircleSelected,
          ]}>
          <Text
            style={[
              styles.staffInitials,
              isSelected && styles.staffInitialsSelected,
            ]}>
            {item.initials}
          </Text>
        </View>
        <Text
          style={[styles.staffName, isSelected && styles.staffNameSelected]}
          numberOfLines={1}>
          {item.name}
        </Text>
      </Pressable>
    );
  };

  return (
    <>
      <View style={styles.sectionWrap}>
        <View style={[styles.top, isDropdownOpen && styles.topJoined]}>
          <Text style={styles.label}>Enter Customer Details</Text>

          {canEditCustomerInput ? (
            <View style={styles.quickPickRow}>
              <TouchableOpacity
                activeOpacity={0.7}
                style={[
                  styles.quickPickChip,
                  isWalkInSelected && !showNewForm && styles.quickPickChipSelected,
                ]}
                onPress={() => {
                  if (showNewForm) {
                    handleCancelNewForm();
                  }
                  onSelectCustomer(WALK_IN_CUSTOMER, null);
                }}>
                <Text
                  style={[
                    styles.quickPickChipText,
                    isWalkInSelected &&
                      !showNewForm &&
                      styles.quickPickChipTextSelected,
                  ]}>
                  {WALK_IN_CUSTOMER}
                </Text>
              </TouchableOpacity>

              {showAddNewCustomerChip ? (
                <TouchableOpacity
                  activeOpacity={0.7}
                  style={[
                    styles.quickPickChip,
                    styles.addNewChipDefault,
                    showNewForm && styles.quickPickChipSelected,
                  ]}
                  onPress={() => {
                    setShowNewForm(prev => {
                      const next = !prev;
                      onNewFormVisibilityChange?.(next);
                      return next;
                    });
                  }}>
                  <View style={styles.addNewChipContent}>
                    <FontAwesome6
                      name="user-plus"
                      iconStyle="solid"
                      size={fs(10)}
                      color={
                        showNewForm
                          ? theme.colors.primaryDark
                          : '#1560BD'
                      }
                    />
                    <Text
                      style={[
                        styles.quickPickChipText,
                        styles.addNewChipTextDefault,
                        showNewForm && styles.quickPickChipTextSelected,
                      ]}>
                      New Customer
                    </Text>
                  </View>
                </TouchableOpacity>
              ) : null}
            </View>
          ) : null}

          {showNewForm ? (
            <View style={styles.newCustomerForm}>
              <View style={styles.newFormField}>
                <FontAwesome6
                  name="user"
                  iconStyle="solid"
                  size={fs(12)}
                  color={theme.colors.textPlaceholder}
                />
                <BottomSheetTextInput
                  value={newName}
                  onChangeText={setNewName}
                  placeholder="Customer name *"
                  placeholderTextColor={theme.colors.textPlaceholder}
                  style={styles.newFormInput}
                  returnKeyType="next"
                  autoCapitalize="words"
                />
              </View>
              <View style={styles.phoneInputOutline}>
                <PhoneInput
                  ref={phoneInputRef}
                  defaultCountry={detectedCountryCode as any}
                  value={newPhoneInput}
                  modalType="popup"
                  placeholder="720 222 222"
                  popularCountries={PREFERRED_COUNTRIES}
                  modalPopularCountriesTitle="Popular countries"
                  modalAllCountriesTitle="All countries"
                  onFocus={() => setNewPhoneTouched(true)}
                  onChangePhoneNumber={value => {
                    let next = value;
                    if (next.startsWith('0')) {
                      next = next.slice(1);
                    }
                    setNewPhoneInput(next);
                    setNewPhoneE164(
                      buildE164FromInput(
                        next,
                        newPhoneCountry ||
                          phoneInputRef.current?.getCountry?.(),
                      ),
                    );
                  }}
                  country={newPhoneCountry}
                  onChangeCountry={country => {
                    setNewPhoneCountry(country);
                    setNewPhoneE164(
                      buildE164FromInput(newPhoneInput, country),
                    );
                  }}
                  phoneInputStyles={{
                    container: {
                      backgroundColor: 'transparent',
                      height: '100%',
                      alignItems: 'center',
                    },
                    flagContainer: {
                      paddingHorizontal: s(8),
                    },
                    divider: {
                      width: borderWidth(
                        Platform.OS === 'web' ? 0.5 : 1,
                      ),
                    },
                    callingCode: {
                      fontFamily: GTFonts.regular,
                      fontSize: fs(14),
                      lineHeight: fs(18),
                      color: theme.colors.text,
                    },
                    input: {
                      fontFamily: GTFonts.regular,
                      fontSize: fs(14),
                      lineHeight: fs(18),
                      color: theme.colors.text,
                      ...(Platform.OS === 'web' && {
                        outlineStyle: 'none',
                      }),
                    } as any,
                  }}
                />
              </View>
              {newPhoneTouched && newPhoneInput.length > 0 && !newPhoneValid ? (
                <Text style={styles.newFormError}>Invalid phone number</Text>
              ) : null}
              <View style={styles.newFormField}>
                <FontAwesome6
                  name="envelope"
                  iconStyle="solid"
                  size={fs(12)}
                  color={theme.colors.textPlaceholder}
                />
                <BottomSheetTextInput
                  value={newEmail}
                  onChangeText={setNewEmail}
                  placeholder="Email (optional)"
                  placeholderTextColor={theme.colors.textPlaceholder}
                  style={styles.newFormInput}
                  returnKeyType="done"
                  keyboardType="email-address"
                  autoCapitalize="none"
                />
              </View>
              {newEmail.trim().length > 0 && !newFormEmailValid ? (
                <Text style={styles.newFormError}>Invalid email address</Text>
              ) : null}
              <View style={styles.newFormActions}>
                <TouchableOpacity
                  activeOpacity={0.7}
                  style={styles.newFormCancelBtn}
                  onPress={handleCancelNewForm}>
                  <Text style={styles.newFormCancelText}>Cancel</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  activeOpacity={0.7}
                  style={[
                    styles.newFormSaveBtn,
                    !canSubmitNewCustomer && styles.newFormSaveBtnDisabled,
                  ]}
                  disabled={!canSubmitNewCustomer}
                  onPress={handleSubmitNewCustomer}>
                  {isCreatingCustomer ? (
                    <ActivityIndicator size="small" color="#fff" />
                  ) : (
                    <Text style={styles.newFormSaveText}>Save to Back Office</Text>
                  )}
                </TouchableOpacity>
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.inputWrap,
                showCustomerDropdown && styles.inputWrapFocused,
                isDropdownOpen && styles.inputWrapJoined,
              ]}>
              <FontAwesome6
                name="user"
                iconStyle="solid"
                size={fs(14)}
                color={
                  showCustomerDropdown
                    ? theme.colors.primaryDark
                    : theme.colors.textPlaceholder
                }
              />
              <BottomSheetTextInput
                value={input}
                onChangeText={text => {
                  if (!canEditCustomerInput) return;
                  setInput(text);
                  onCustomerInputFocus();
                }}
                onFocus={() => {
                  if (canEditCustomerInput) {
                    onCustomerInputFocus();
                  }
                }}
                placeholder="Customer name / Table no"
                placeholderTextColor={theme.colors.textPlaceholder}
                style={[
                  styles.input,
                  !canEditCustomerInput && styles.disabledInput,
                ]}
                returnKeyType="done"
                editable={canEditCustomerInput}
                onSubmitEditing={onSubmitName}
              />
              {canEditCustomerInput && input.length > 0 ? (
                <TouchableOpacity
                  activeOpacity={0.7}
                  style={styles.clearButton}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  onPress={() => {
                    setInput('');
                    onCustomerInputFocus();
                  }}>
                  <FontAwesome6
                    name="xmark"
                    iconStyle="solid"
                    size={fs(12)}
                    color={theme.colors.textSecondary}
                  />
                </TouchableOpacity>
              ) : null}
            </View>
          )}
        </View>
        {isDropdownOpen && !showNewForm ? (
          <View style={styles.dropdown}>
            <Text style={styles.dropdownHeader}>Recent customers</Text>
            <View style={styles.dropdownList}>
              <BottomSheetFlatList
                data={customerSuggestions}
                keyExtractor={(c, i) => `${c.name}-${i}`}
                renderItem={renderCustomerItem}
                ItemSeparatorComponent={() => (
                  <View style={styles.customerSeparator} />
                )}
                keyboardShouldPersistTaps="handled"
                scrollEnabled={customerSuggestions.length > MAX_DROPDOWN_ITEMS}
                nestedScrollEnabled
                showsVerticalScrollIndicator={
                  customerSuggestions.length > MAX_DROPDOWN_ITEMS
                }
                initialNumToRender={MAX_DROPDOWN_ITEMS}
                windowSize={3}
                removeClippedSubviews={Platform.OS !== 'web'}
              />
            </View>
          </View>
        ) : null}
      </View>

      {shouldShowStaffSection && !showNewForm ? (
        <View style={styles.sectionWrap}>
          <Text style={styles.label}>Assign Staff</Text>

          {isLoadingStaff ? (
            <View style={styles.staffLoadingWrap}>
              <ActivityIndicator size="small" color={theme.colors.primaryDark} />
              <Text style={styles.staffLoadingText}>Loading staff...</Text>
            </View>
          ) : (
            <BottomSheetFlatList
              horizontal
              data={filteredStaff}
              keyExtractor={item => item.id}
              showsHorizontalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={styles.staffChipsRow}
              renderItem={renderStaffItem}
              ListEmptyComponent={
                <Text style={styles.staffEmptyText}>
                  No matching staff found. You can still save the order.
                </Text>
              }
            />
          )}

          {filteredStaff?.length > 6 && <View style={styles.staffSearchRow}>
            <FontAwesome6
              name="magnifying-glass"
              iconStyle="solid"
              size={fs(12)}
              color={theme.colors.textPlaceholder}
            />
            <BottomSheetTextInput
              value={staffQuery}
              onChangeText={setStaffQuery}
              placeholder="Search staff..."
              placeholderTextColor={theme.colors.textPlaceholder}
              style={styles.staffSearchInput}
              returnKeyType="done"
            />
          </View>}
        </View>
      ) : null}
    </>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    sectionWrap: {
      marginBottom: v(10),
    },
    top: {
      // marginTop: v(10),
      // marginBottom: v(10),
    },
    topJoined: {
      marginBottom: 0,
    },
    label: {
      fontSize: fs(18),
      lineHeight: fs(20),
      fontFamily: GTFonts.semibold,
      marginBottom: v(16),
      color: theme.colors.text,
    },
    quickPickRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      marginBottom: v(10),
    },
    quickPickChip: {
      paddingHorizontal: s(12),
      paddingVertical: v(6),
      borderRadius: theme.borderRadius.round,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.border,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    quickPickChipSelected: {
      backgroundColor: theme.colors.primaryLight,
      borderColor: theme.colors.primary,
    },
    quickPickChipText: {
      fontSize: fs(12),
      lineHeight: fs(16),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    quickPickChipTextSelected: {
      color: theme.colors.primaryDark,
      fontFamily: GTFonts.semibold,
    },
    addNewChipContent: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(5),
    },
    addNewChipDefault: {
      backgroundColor: '#EBF5FF',
      borderColor: '#B3D4F7',
    },
    addNewChipIcon: {
      color: '#1560BD',
    },
    addNewChipTextDefault: {
      color: '#1560BD',
      fontFamily: GTFonts.semibold,
    },
    newCustomerForm: {
      borderWidth: borderWidth(1.5),
      borderColor: theme.colors.borderGray,
      borderRadius: s(theme.borderRadius.md),
      padding: s(14),
      gap: v(14),
      backgroundColor: theme.colors.surface,
    },
    newFormField: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray,
      borderRadius: s(theme.borderRadius.sm),
      height: v(50),
      paddingHorizontal: s(14),
      gap: s(10),
      backgroundColor: theme.colors.surfaceSecondary,
      ...(Platform.OS === 'web' && { height: v(54) }),
    },
    newFormInput: {
      flex: 1,
      height: '100%',
      fontSize: fs(15),
      lineHeight: fs(20),
      fontFamily: GTFonts.regular,
      color: theme.colors.text,
    },
    phoneInputOutline: {
      backgroundColor: theme.colors.surfaceSecondary,
      height: v(50),
      borderRadius: s(theme.borderRadius.sm),
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray,
      overflow: 'hidden',
      ...(Platform.OS === 'web' && { height: v(54) }),
    },
    newFormError: {
      fontSize: fs(11),
      lineHeight: fs(14),
      color: theme.colors.error || '#D32F2F',
      fontFamily: GTFonts.regular,
      marginTop: v(-4),
      paddingHorizontal: s(4),
    },
    newFormActions: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: s(10),
      marginTop: v(2),
    },
    newFormCancelBtn: {
      paddingHorizontal: s(14),
      paddingVertical: v(8),
      borderRadius: s(theme.borderRadius.sm),
    },
    newFormCancelText: {
      fontSize: fs(13),
      lineHeight: fs(16),
      fontFamily: GTFonts.semibold,
      color: theme.colors.textSecondary,
    },
    newFormSaveBtn: {
      paddingHorizontal: s(16),
      paddingVertical: v(8),
      borderRadius: s(theme.borderRadius.sm),
      backgroundColor: theme.colors.primaryDark,
      minWidth: s(110),
      alignItems: 'center',
    },
    newFormSaveBtnDisabled: {
      opacity: 0.5,
    },
    newFormSaveText: {
      fontSize: fs(13),
      lineHeight: fs(16),
      fontFamily: GTFonts.semibold,
      color: '#fff',
    },
    inputWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray,
      borderRadius: s(theme.borderRadius.md),
      height: v(50),
      paddingHorizontal: s(14),
      gap: s(8),
      ...(Platform.OS === 'web' && { height: v(56) }),
    },
    inputWrapFocused: {
      borderColor: theme.colors.borderGray,
      borderWidth: borderWidth(1.5),
      backgroundColor: theme.colors.surface,
    },
    // Squares off the bottom edge so the field and its suggestion list read as a
    // single control instead of two stacked cards.
    inputWrapJoined: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    },
    clearButton: {
      justifyContent: 'center',
      alignItems: 'center',
      height: s(24),
      width: s(24),
      borderRadius: s(12),
      backgroundColor: theme.colors.surfaceSecondary,
    },
    input: {
      flex: 1,
      height: '100%',
      fontSize: fs(16),
      lineHeight: fs(20),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    disabledInput: {
      color: theme.colors.text,
    },
    dropdown: {
      borderWidth: borderWidth(1.5),
      borderTopWidth: 0,
      borderColor: theme.colors.borderGray,
      borderBottomLeftRadius: s(theme.borderRadius.md),
      borderBottomRightRadius: s(theme.borderRadius.md),
      backgroundColor: theme.colors.surface,
      overflow: 'hidden',
    },
    dropdownHeader: {
      fontSize: fs(10),
      lineHeight: fs(14),
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: theme.colors.textTertiary,
      fontFamily: GTFonts.semibold,
      paddingHorizontal: s(14),
      paddingTop: v(8),
      paddingBottom: v(4),
    },
    dropdownList: {
      maxHeight: v(CUSTOMER_ITEM_HEIGHT * MAX_DROPDOWN_ITEMS),
    },
    customerItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(10),
      height: v(CUSTOMER_ITEM_HEIGHT),
      paddingHorizontal: s(14),
    },
    customerItemSelected: {
      backgroundColor: theme.colors.infoLight,
    },
    // Inset past the avatar so the rule starts at the name column.
    customerSeparator: {
      height: StyleSheet.hairlineWidth,
      marginLeft: s(52),
      backgroundColor: theme.colors.borderSecondary || theme.colors.border,
    },
    customerAvatar: {
      width: s(28),
      height: s(28),
      borderRadius: s(14),
      justifyContent: 'center',
      alignItems: 'center',
    },
    cloudBadge: {
      position: 'absolute' as const,
      bottom: -1,
      right: -1,
      width: s(12),
      height: s(12),
      borderRadius: s(6),
      backgroundColor: '#EBF2FA',
      justifyContent: 'center',
      alignItems: 'center',
    },
    customerAvatarText: {
      fontSize: fs(10),
      lineHeight: fs(13),
      color: AVATAR_INITIALS_COLOR,
      fontFamily: GTFonts.semibold,
    },
    customerName: {
      flex: 1,
      fontSize: fs(14),
      color: theme.colors.text,
      fontFamily: PoppinsFonts.regular,
    },
    customerNameSelected: {
      color: theme.colors.primaryDark,
      fontFamily: PoppinsFonts.semibold,
    },
    customerMeta: {
      fontSize: fs(11),
      lineHeight: fs(14),
      color: theme.colors.textTertiary,
      fontFamily: GTFonts.regular,
    },
    staffChipsRow: {
      paddingTop: v(4),
      paddingBottom: v(12),
    },
    staffChipWrap: {
      alignItems: 'center',
      marginRight: s(16),
    },
    staffCircle: {
      width: s(54),
      height: s(54),
      borderRadius: s(27),
      borderWidth: borderWidth(1.5),
      borderColor: '#D6DEE8',
      justifyContent: 'center',
      alignItems: 'center',
    },
    staffCircleSelected: {
      borderColor: '#1560BD',
      borderWidth: borderWidth(3),
    },
    staffInitials: {
      color: AVATAR_INITIALS_COLOR,
      fontSize: fs(16),
      fontFamily: GTFonts.semibold,
    },
    staffInitialsSelected: {
      color: '#1560BD',
    },
    staffName: {
      marginTop: v(5),
      color: theme.colors.textSecondary,
      fontSize: fs(11),
      lineHeight: fs(14),
      fontFamily: GTFonts.regular,
      textAlign: 'center',
    },
    staffNameSelected: {
      color: '#1560BD',
      fontFamily: GTFonts.semibold,
    },
    staffSearchRow: {
      flexDirection: 'row',
      alignItems: 'center',
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderGray,
      borderRadius: s(theme.borderRadius.md),
      height: v(44),
      paddingHorizontal: s(12),
      gap: s(8),
      marginTop: v(4),
    },
    staffSearchInput: {
      flex: 1,
      height: '100%',
      color: theme.colors.text,
      fontSize: fs(13),
      lineHeight: fs(18),
      fontFamily: GTFonts.regular,
    },
    staffEmptyText: {
      color: theme.colors.textPlaceholder,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
      lineHeight: fs(18),
      paddingVertical: v(8),
    },
    staffLoadingWrap: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      paddingVertical: v(12),
    },
    staffLoadingText: {
      color: theme.colors.textTertiary,
      fontFamily: GTFonts.regular,
      fontSize: fs(12),
    },
  });

export default OrderCustomerStaffSection;
